import { useEffect, useMemo, useRef, useState } from 'react';
import api, { formatApiErrorDetail } from '../../lib/api';
import { supabase } from '../../lib/supabase';
import { F, Btn } from '../../lib/bricks';
import {
  ACCEPT, MULTIPART_OVER, FIELD_LABEL, importError, parseLinks, todayIso, pairedPreviews, fileLine,
  providerLine, captureLine, nameOf,
} from './captureImportsModel';

const MUTED = 'var(--c-ink-mute)';
const LINE = `1px solid var(--c-hair-s)`;
const BAD = 'var(--c-bad)';
const field = { padding: '10px 12px', minHeight: 44, border: '1px solid var(--c-line)', borderRadius: 2, fontSize: 14, minWidth: 0, width: '100%', boxSizing: 'border-box', fontFamily: F.display, background: 'var(--c-field)', color: 'var(--c-ink)' };
const label = { display: 'block', fontSize: 12, color: MUTED, marginBottom: 6 };
const quiet = { minHeight: 44, padding: '0 14px', fontSize: 13 };
const row = { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: '0 16px', alignItems: 'center', borderTop: LINE, padding: '12px 0', minHeight: 44 };
const CSS = `.cip-two{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px}@media (max-width:719px){.cip-two{grid-template-columns:1fr}}`;

function errText(e, fallback) {
  return formatApiErrorDetail(e?.response?.data?.detail) || (e && e.message && !e.response ? '' : '') || fallback;
}

async function authHeaders() {
  try {
    const { data } = await supabase.auth.getSession();
    const t = data && data.session && data.session.access_token;
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}

// Default uploader: @vercel/blob/client uploadPresigned against our handleUploadUrl.
export async function blobUpload(projectId, file, onProgress) {
  const { data } = await api.post(`/dev/projects/${projectId}/imports/upload-path`, { name: file.name, bytes: file.size });
  const { uploadPresigned } = await import('@vercel/blob/client');
  const base = (api.defaults && api.defaults.baseURL) || '/api';
  await uploadPresigned(data.pathname, file, {
    access: 'private',
    handleUploadUrl: `${base}/dev/projects/${projectId}/imports/upload`,
    clientPayload: JSON.stringify({ bytes: file.size }),
    multipart: file.size > MULTIPART_OVER,
    headers: await authHeaders(),
    onUploadProgress: (p) => onProgress && onProgress(Math.round(p.percentage || 0)),
  });
  return { pathname: data.pathname, name: file.name, bytes: file.size };
}

function ImportForm({ projectId, phases, upload, uploader, onDone, onCancel }) {
  const [date, setDate] = useState(todayIso());
  const [phaseId, setPhaseId] = useState('');
  const [files, setFiles] = useState([]);
  const [links, setLinks] = useState('');
  const [progress, setProgress] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const input = useRef(null);
  const linkList = parseLinks(links);
  const previews = useMemo(() => pairedPreviews([...files.map((f) => f.name), ...linkList.map(nameOf)]), [files, links]); // eslint-disable-line

  async function submit(e) {
    e.preventDefault();
    const msg = importError({ date, files, links: linkList });
    setErr(msg);
    if (msg) return;
    setBusy(true);
    try {
      const sent = [];
      for (const f of files) {
        sent.push(await uploader(projectId, f, (pct) => setProgress((p) => ({ ...p, [f.name]: pct }))));
        setProgress((p) => ({ ...p, [f.name]: 100 }));
      }
      await api.post(`/dev/projects/${projectId}/imports`, {
        captured_on: date, phase_id: phaseId || null, files: [...sent, ...linkList.map((url) => ({ url }))],
      });
      onDone();
    } catch (ex) {
      setErr(errText(ex, 'Couldn’t import these files. Nothing was added to the timeline.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} data-testid="import-form" data-report-surface="" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 640, marginTop: 12, paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))' }}>
      <div className="cip-two">
        <div><label style={label} htmlFor="ci-date">Captured on</label><input id="ci-date" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} style={field} /></div>
        <div>
          <label style={label} htmlFor="ci-phase">Phase (optional)</label>
          <select id="ci-phase" value={phaseId} onChange={(e) => setPhaseId(e.target.value)} style={field}>
            <option value="">Whole site</option>
            {(phases || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      {upload && upload.enabled ? (
        <div>
          <span style={label}>Files</span>
          <input ref={input} type="file" multiple accept={ACCEPT} data-testid="import-files" style={{ display: 'none' }}
            onChange={(e) => { setFiles([...files, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
          <Btn type="button" variant="ghost" style={quiet} onClick={() => input.current && input.current.click()}>Choose files</Btn>
          <p style={{ fontSize: 12.5, color: MUTED, margin: '8px 0 0' }}>GeoTIFF, LAS, LAZ, E57, PLY, OBJ, GLB, photos, video or PDF. Up to 5 GB each.</p>
          {files.length ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', borderBottom: LINE }}>
              {files.map((f, i) => (
                <li key={`${f.name}-${i}`} data-testid="import-file" style={row}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: MUTED, marginTop: 2, fontFamily: F.mono, fontVariantNumeric: 'tabular-nums' }}>
                      {fileLine(f.name, f.size, previews)}{progress[f.name] != null ? ` · ${progress[f.name]}%` : ''}
                    </span>
                  </span>
                  {!busy ? <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}
                    style={{ minHeight: 44, minWidth: 44, background: 'none', border: 'none', color: 'var(--c-ink)', fontSize: 13, cursor: 'pointer', fontFamily: F.display }}>Remove</button> : <span />}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : (
        <p data-testid="uploads-off" style={{ fontSize: 13, color: MUTED, margin: 0 }}>Uploads aren’t switched on here yet. Paste links to your files instead.</p>
      )}
      <div>
        <label style={label} htmlFor="ci-links">{upload && upload.enabled ? 'Or paste links, one per line' : 'Links, one per line'}</label>
        <textarea id="ci-links" value={links} onChange={(e) => setLinks(e.target.value)} rows={3} placeholder="https://…/orthomosaic.tif"
          style={{ ...field, minHeight: 88, fontFamily: F.mono, fontSize: 12.5 }} />
      </div>
      {err ? <p role="alert" style={{ color: BAD, fontSize: 12.5, margin: 0 }}>{err}</p> : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Btn type="submit" variant="accent" disabled={busy} data-testid="import-submit">{busy ? 'Importing…' : 'Add to timeline'}</Btn>
        <Btn type="button" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Btn>
      </div>
    </form>
  );
}

function ProviderRow({ projectId, p, phases, onChanged, onImported }) {
  const [mode, setMode] = useState(null); // 'connect' | 'browse'
  const [creds, setCreds] = useState({});
  const [caps, setCaps] = useState(null);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const connected = p.status === 'connected';

  async function connect(e) {
    e.preventDefault();
    setBusy('connect'); setErr('');
    try {
      await api.post(`/dev/imports/${p.id}/connect`, { credentials: creds });
      setCreds({}); setMode(null); onChanged();
    } catch (ex) {
      setErr(errText(ex, `Couldn’t connect ${p.name}. Nothing was saved.`));
    } finally { setBusy(''); }
  }

  async function browse() {
    setMode('browse'); setErr(''); setMsg(''); setCaps(null);
    try {
      const r = await api.get(`/dev/projects/${projectId}/imports/${p.id}/captures`);
      setCaps((r.data && r.data.captures) || []);
    } catch (ex) {
      setCaps([]); setErr(errText(ex, `Couldn’t reach ${p.name}. Try again.`));
    }
  }

  async function importOne(c) {
    setBusy(c.id); setErr(''); setMsg('');
    try {
      const r = await api.post(`/dev/projects/${projectId}/imports/${p.id}`, { capture_id: c.id });
      if (r.status === 202 || (r.data && r.data.status === 'pending')) { setMsg(r.data.detail); return; }
      setCaps((cs) => cs.map((x) => (x.id === c.id ? { ...x, imported: true } : x)));
      onImported();
    } catch (ex) {
      setErr(errText(ex, 'Couldn’t import this capture. Nothing was added.'));
    } finally { setBusy(''); }
  }

  async function disconnect() {
    setBusy('disconnect'); setErr('');
    try { await api.delete(`/dev/imports/${p.id}/connect`); setMode(null); onChanged(); } catch (ex) {
      setErr(errText(ex, 'Couldn’t disconnect right now.'));
    } finally { setBusy(''); }
  }

  const action = connected
    ? <Btn variant="ghost" style={quiet} onClick={mode === 'browse' ? () => setMode(null) : browse}>{mode === 'browse' ? 'Close' : 'Choose a capture'}</Btn>
    : (p.status === 'ready' || p.status === 'needs_reconnect')
      ? <Btn variant="ghost" style={quiet} onClick={() => setMode(mode === 'connect' ? null : 'connect')}>{mode === 'connect' ? 'Close' : 'Connect'}</Btn>
      : null;
  return (
    <li data-testid={`provider-${p.id}`} style={{ borderTop: LINE }}>
      <div style={{ ...row, borderTop: 'none' }}>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 14.5 }}>{p.name}</span>
          <span style={{ display: 'block', fontSize: 12.5, color: MUTED, marginTop: 2 }}>{providerLine(p)}</span>
        </span>
        {action}
      </div>
      {mode === 'connect' ? (
        <form onSubmit={connect} style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 16, maxWidth: 480 }}>
          <p style={{ fontSize: 12.5, color: MUTED, margin: 0 }}>{p.needs}</p>
          {(p.fields || []).map((f) => (
            <div key={f}><label style={label} htmlFor={`ci-${p.id}-${f}`}>{FIELD_LABEL[f] || f}</label>
              <input id={`ci-${p.id}-${f}`} type="password" autoComplete="off" value={creds[f] || ''} onChange={(e) => setCreds({ ...creds, [f]: e.target.value })} style={field} /></div>
          ))}
          <div><Btn type="submit" variant="primary" disabled={busy === 'connect'}>{busy === 'connect' ? 'Checking…' : `Connect ${p.name}`}</Btn></div>
        </form>
      ) : null}
      {mode === 'browse' ? (
        <div style={{ paddingBottom: 12 }}>
          {caps === null ? <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>Loading captures…</p> : null}
          {caps && !caps.length && !err ? <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>No processed captures in this account yet.</p> : null}
          {caps && caps.length ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {caps.map((c) => (
                <li key={c.id} data-testid="provider-capture" style={{ ...row, paddingLeft: 0 }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14 }}>{c.name}</span>
                    <span style={{ display: 'block', fontSize: 12.5, color: MUTED, marginTop: 2 }}>{captureLine(c)}</span>
                  </span>
                  {c.imported ? <span style={{ fontSize: 13, color: MUTED }}>On the timeline</span>
                    : <Btn variant="ghost" style={quiet} disabled={!!busy} onClick={() => importOne(c)}>{busy === c.id ? 'Importing…' : 'Import'}</Btn>}
                </li>
              ))}
            </ul>
          ) : null}
          {!p.sandbox ? <button type="button" onClick={disconnect} disabled={busy === 'disconnect'}
            style={{ marginTop: 8, minHeight: 44, background: 'none', border: 'none', padding: 0, color: MUTED, fontSize: 13, cursor: 'pointer', fontFamily: F.display, textDecoration: 'underline' }}>Disconnect {p.name}</button> : null}
        </div>
      ) : null}
      {msg ? <p style={{ fontSize: 12.5, color: MUTED, margin: '0 0 12px' }}>{msg}</p> : null}
      {err ? <p role="alert" style={{ color: BAD, fontSize: 12.5, margin: '0 0 12px' }}>{err}</p> : null}
    </li>
  );
}

export default function CaptureImportsPanel({ projectId, phases, onImported, uploader = blobUpload }) {
  const [opts, setOpts] = useState(null);
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try {
      const r = await api.get(`/dev/projects/${projectId}/imports`);
      setOpts(r.data); setError('');
    } catch (e) {
      setOpts(null); setError(errText(e, 'Imports aren’t available right now.'));
    }
  }
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const providers = ((opts && opts.providers) || []).filter((p) => p.status !== 'off');
  const comingSoon = ((opts && opts.providers) || []).filter((p) => p.status === 'off').map((p) => p.name);

  return (
    <div style={{ borderTop: LINE, paddingTop: 16, margin: '4px 0 20px' }} data-testid="capture-imports">
      <style>{CSS}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 500 }}>Your own captures</h3>
        {!open && opts ? <Btn variant="ghost" style={quiet} onClick={() => { setOpen(true); setDone(''); }} data-testid="import-open">Import files</Btn> : null}
      </div>
      <p style={{ fontSize: 13, lineHeight: 1.55, color: MUTED, margin: '6px 0 12px', maxWidth: 560 }}>
        Add a map, scan or model from another flight to this timeline. It’s marked as imported, so reports never say Bricks captured it.
      </p>
      {open ? <ImportForm projectId={projectId} phases={phases} upload={opts && opts.upload} uploader={uploader}
        onCancel={() => setOpen(false)} onDone={() => { setOpen(false); setDone('Added to the timeline.'); onImported && onImported(); }} /> : null}
      {done ? <p role="status" style={{ fontSize: 13, color: 'var(--c-ink)', margin: '0 0 12px' }}>{done}</p> : null}
      {providers.length ? (
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', borderBottom: LINE }}>
          {providers.map((p) => <ProviderRow key={p.id} projectId={projectId} p={p} phases={phases} onChanged={load} onImported={() => onImported && onImported()} />)}
        </ul>
      ) : null}
      {comingSoon.length ? <p data-testid="providers-soon" style={{ fontSize: 12.5, color: MUTED, margin: '8px 0 0' }}>{`Imports straight from ${listWords(comingSoon)} are coming. Export the files and add them here for now.`}</p> : null}
      {error ? <p role="alert" style={{ color: BAD, fontSize: 12.5, margin: '8px 0 0' }}>{error}</p> : null}
    </div>
  );
}

function listWords(xs) {
  return xs.length < 2 ? xs.join('') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}
